import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { cajasAPI, cierresCajaAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { imprimirCierreCaja } from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';
import '../Clientes.css';

const formatMoney = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CLAVE_FILTROS = 'cierres_caja_listado_filtros';

const CierresCajaListado = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { logoUrl } = useBranding();
  const [cajas, setCajas] = useState([]);
  const [filtroCaja, setFiltroCaja] = useState(() => {
    const saved = loadFiltrosSession(CLAVE_FILTROS, { caja_id: '' });
    return saved.caja_id || '';
  });
  const [lista, setLista] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const primeraCargaListaRef = useRef(true);
  const [refrescandoLista, setRefrescandoLista] = useState(false);
  const cargaListaSeqRef = useRef(0);

  useEffect(() => {
    saveFiltrosSession(CLAVE_FILTROS, { caja_id: filtroCaja });
  }, [filtroCaja]);

  useEffect(() => {
    cajasAPI.getAll({}).then((r) => { if (r.success) setCajas(r.data || []); }).catch(() => {});
  }, []);

  const loadLista = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaListaRef.current;
    const seq = ++cargaListaSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescandoLista(true);
      const params = {};
      if (filtroCaja) params.caja_id = filtroCaja;
      const res = await cierresCajaAPI.listar(params);
      if (seq !== cargaListaSeqRef.current) return;
      if (res.success) setLista(res.data || []);
    } catch (e) {
      if (seq !== cargaListaSeqRef.current) return;
      toast.error(e.message || 'Error al cargar cierres');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaListaRef.current = false;
      }
      if (seq === cargaListaSeqRef.current) setRefrescandoLista(false);
    }
  }, [filtroCaja]);

  useEffect(() => {
    if (!id) loadLista();
  }, [id, loadLista]);

  useEffect(() => {
    if (!id) {
      setDetalle(null);
      return;
    }
    setLoading(true);
    cierresCajaAPI.getById(id)
      .then((res) => {
        if (res.success) setDetalle(res.data);
        else toast.error(res.message);
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const anular = async (cierreId) => {
    if (!window.confirm('¿Anular este cierre? Si tiene ajuste, se revertirá.')) return;
    try {
      const res = await cierresCajaAPI.anular(cierreId);
      if (res.success) {
        toast.success('Cierre anulado');
        if (id) setDetalle(res.data);
        else loadLista();
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const imprimirDetalle = (cc) => {
    if (!cc) return;
    try {
      imprimirCierreCaja({
        titulo: `Cierre N° ${cc.numero} — ${cc.caja_nombre}`,
        subtitulo: `Fecha: ${cc.fecha_cierre} | Usuario: ${cc.usuario_nombre || ''} | Estado: ${cc.estado}`,
        resumen: [
          { label: 'Saldo inicial día', value: formatMoney(cc.saldo_inicial_dia) },
          { label: 'Ingresos del día', value: formatMoney(cc.total_ingresos_dia) },
          { label: 'Egresos del día', value: formatMoney(cc.total_egresos_dia) },
          { label: 'Saldo sistema total', value: formatMoney(cc.saldo_sistema_total) },
          { label: 'Efectivo sistema', value: formatMoney(cc.saldo_sistema_efectivo) },
          { label: 'Cheques sistema', value: formatMoney(cc.saldo_sistema_cheques) },
          { label: 'Físico efectivo', value: formatMoney(cc.total_fisico_efectivo) },
          { label: 'Físico cheques', value: formatMoney(cc.total_fisico_cheques) },
        ],
        efectivo: (cc.efectivo || []).map((e) => ({
          nombre: e.nombre,
          cantidad: e.cantidad,
          subtotal: parseFloat(e.subtotal) || 0,
        })),
        cheques: (cc.cheques || []).map((ch) => ({
          numero: ch.numero,
          cliente_razon_social: ch.cliente_razon_social,
          importe: parseFloat(ch.importe) || 0,
          marcado_fisico: !!ch.marcado_fisico,
        })),
        diferencias: {
          efectivo: cc.diferencia_efectivo,
          cheques: cc.diferencia_cheques,
          total: cc.diferencia_total,
        },
        ajuste: cc.movimiento_ajuste
          ? {
              id: cc.movimiento_ajuste.id,
              tipo: cc.movimiento_ajuste.tipo,
              monto: cc.movimiento_ajuste.monto,
              concepto: cc.movimiento_ajuste.concepto,
            }
          : null,
        observaciones: cc.observaciones || '',
        logoUrl,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  if (!hasPermission('cerrar_caja') && !hasPermission('ver_fondos_listado')) {
    return <div className="clientes"><p>Sin permiso.</p></div>;
  }

  if (id && detalle) {
    return (
      <div className="clientes pagina-listado pagina-listado--scroll">
        <div className="page-header">
          <h1>Cierre N° {detalle.numero} — {detalle.caja_nombre}</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-secondary" onClick={() => navigate('/cierres-caja')}>Volver</button>
            <button type="button" className="btn-secondary" onClick={() => imprimirDetalle(detalle)}>Imprimir</button>
            {hasPermission('cerrar_caja') && detalle.estado === 'confirmado' && (
              <button type="button" className="btn-danger" onClick={() => anular(detalle.id)}>Anular</button>
            )}
          </div>
        </div>
        <div className="listado-panel clientes-listado listado-controles">
          <div className="listado-panel-cuerpo clientes-listado-cuerpo">
            <div className="listado-tabla-area clientes-listado-tabla-area">
        <div className="table-container" style={{ padding: 16, marginBottom: 16 }}>
          <p><strong>Fecha:</strong> {detalle.fecha_cierre} | <strong>Estado:</strong> {detalle.estado}</p>
          <p><strong>Usuario:</strong> {detalle.usuario_nombre}</p>
          {detalle.observaciones && <p><strong>Observaciones:</strong> {detalle.observaciones}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
            <div><strong>Saldo sistema total:</strong><br />{formatMoney(detalle.saldo_sistema_total)}</div>
            <div><strong>Efectivo sistema:</strong><br />{formatMoney(detalle.saldo_sistema_efectivo)}</div>
            <div><strong>Cheques sistema:</strong><br />{formatMoney(detalle.saldo_sistema_cheques)}</div>
            <div><strong>Físico efectivo:</strong><br />{formatMoney(detalle.total_fisico_efectivo)}</div>
            <div><strong>Físico cheques:</strong><br />{formatMoney(detalle.total_fisico_cheques)}</div>
            <div>
              <strong>Diferencia total:</strong><br />
              <span style={{ color: Math.abs(parseFloat(detalle.diferencia_total) || 0) < 0.01 ? 'inherit' : 'crimson' }}>
                {formatMoney(detalle.diferencia_total)}
              </span>
            </div>
          </div>
          {detalle.movimiento_ajuste ? (
            <div style={{ marginTop: 16, padding: 12, background: '#f8f9fa', borderRadius: 6, border: '1px solid #dee2e6' }}>
              <strong>Ajuste registrado</strong>
              <p style={{ margin: '8px 0 0' }}>
                Movimiento #{detalle.movimiento_ajuste.id} —{' '}
                {detalle.movimiento_ajuste.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} de{' '}
                {formatMoney(detalle.movimiento_ajuste.monto)}
              </p>
              <p style={{ margin: '4px 0 0', color: '#555', fontSize: '0.95em' }}>
                {detalle.movimiento_ajuste.concepto}
              </p>
            </div>
          ) : detalle.movimiento_ajuste_id ? (
            <p style={{ marginTop: 12 }}><strong>Ajuste registrado:</strong> movimiento #{detalle.movimiento_ajuste_id}</p>
          ) : null}
          {!detalle.movimiento_ajuste && !detalle.movimiento_ajuste_id && Math.abs(parseFloat(detalle.diferencia_total) || 0) >= 0.01 && (
            <p style={{ marginTop: 12, color: '#666' }}>Sin movimiento de ajuste (diferencia no corregida en fondos).</p>
          )}
        </div>
        <div className="table-container" style={{ marginBottom: 16 }}>
          <h2>Arqueo efectivo</h2>
          <table className="data-table">
            <thead><tr><th>Denominación</th><th>Cantidad</th><th>Subtotal</th></tr></thead>
            <tbody>
              {(detalle.efectivo || []).map((e) => (
                <tr key={e.id}>
                  <td>{e.nombre}</td>
                  <td>{e.cantidad}</td>
                  <td>{formatMoney(e.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="table-container">
          <h2>Cheques</h2>
          <table className="data-table">
            <thead>
              <tr><th>N°</th><th>Cliente</th><th>Importe</th><th>En sistema</th><th>Marcado físico</th></tr>
            </thead>
            <tbody>
              {(detalle.cheques || []).map((ch) => (
                <tr key={ch.id}>
                  <td>{ch.numero}</td>
                  <td>{ch.cliente_razon_social || '—'}</td>
                  <td>{formatMoney(ch.importe)}</td>
                  <td>{ch.en_sistema ? 'Sí' : 'No'}</td>
                  <td>{ch.marcado_fisico ? 'Sí' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (id && loading) {
    return <div className="loading">Cargando…</div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Historial de cierres de caja</h1>
        {hasPermission('cerrar_caja') && (
          <Link to="/cierre-caja" className="btn-primary">+ Nuevo cierre</Link>
        )}
      </div>
      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters-section" style={{ marginBottom: 0 }}>
          <div className="filter-group">
            <label>Filtrar por caja</label>
            <select value={filtroCaja} onChange={(e) => setFiltroCaja(e.target.value)}>
              <option value="">Todas</option>
              {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
        </div>
        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className={`listado-tabla-area clientes-listado-tabla-area${refrescandoLista ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>N°</th>
              <th>Caja</th>
              <th>Fecha</th>
              <th>Saldo sistema</th>
              <th>Diferencia</th>
              <th>Estado</th>
              <th>Usuario</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="no-data">Cargando…</td></tr>
            ) : lista.length === 0 ? (
              <tr><td colSpan={8} className="no-data">Sin cierres registrados</td></tr>
            ) : (
              lista.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.numero}</strong></td>
                  <td>{row.caja_nombre}</td>
                  <td>{row.fecha_cierre}</td>
                  <td>{formatMoney(row.saldo_sistema_total)}</td>
                  <td style={{ color: Math.abs(parseFloat(row.diferencia_total)) < 0.01 ? 'inherit' : 'crimson' }}>
                    {formatMoney(row.diferencia_total)}
                  </td>
                  <td>{row.estado}</td>
                  <td>{row.usuario_nombre}</td>
                  <td>
                    <Link to={`/cierres-caja/${row.id}`} className="btn-edit">Ver</Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
            </div>
          {refrescandoLista && (
            <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
              <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
              <span className="clientes-listado-refresh-label">Actualizando…</span>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CierresCajaListado;
